/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingIndicator } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingIndicator';

import type { RemixOptionKind } from '../../../index.ts';

const messages = defineMessages({
	titleChart: {
		id: 'malleable-ui-components.remix-thinking-bar.title-chart',
		defaultMessage: 'Rovo is building your chart',
		description: 'Text displayed while Rovo is generating a chart remix',
	},
	titleDiagram: {
		id: 'malleable-ui-components.remix-thinking-bar.title-diagram',
		defaultMessage: 'Rovo is building your diagram',
		description: 'Text displayed while Rovo is generating a diagram remix',
	},
	titleInfographic: {
		id: 'malleable-ui-components.remix-thinking-bar.title-infographic',
		defaultMessage: 'Rovo is building your infographic',
		description: 'Text displayed while Rovo is generating an infographic remix',
	},
	titleVisualization: {
		id: 'malleable-ui-components.remix-thinking-bar.title-visualization',
		defaultMessage: 'Rovo is building your visualization',
		description: 'Text displayed while Rovo is generating a visualization remix',
	},
	titleCustom: {
		id: 'malleable-ui-components.remix-thinking-bar.title-custom',
		defaultMessage: 'Rovo is building your custom remix',
		description: 'Text displayed while Rovo is generating a custom remix',
	},
	escHint: {
		id: 'malleable-ui-components.remix-thinking-bar.esc-hint',
		defaultMessage: 'Esc',
		description: 'Keyboard hint showing the Esc key can be used to cancel',
	},
	stopLabel: {
		id: 'malleable-ui-components.remix-thinking-bar.stop-label',
		defaultMessage: 'Stop remixing',
		description: 'Accessible label for the stop button',
	},
});

const thinkingTitleByOptionType: Record<RemixOptionKind, MessageDescriptor> = {
	chart: messages.titleChart,
	diagram: messages.titleDiagram,
	infographic: messages.titleInfographic,
	'visual-aid': messages.titleVisualization,
	custom: messages.titleCustom,
};

const styles = cssMap({
	fixedWrapper: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		paddingInline: token('space.200'),
		paddingBlock: token('space.100'),
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		minWidth: '300px',
	},
	escBadge: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},
});

type RemixThinkingBarProps = {
	onStop: () => void;
	optionType?: RemixOptionKind;
	title?: string;
};

/**
 * A floating thinking bar that displays a Rovo loading animation, status text,
 * an Esc keyboard hint, and a stop button. Matches the visual style of the
 * inline chat ThinkingView / AI loading screen.
 */
export function RemixThinkingBar({ onStop, optionType, title }: RemixThinkingBarProps) {
	const { formatMessage } = useIntl();

	const thinkingTitle =
		title ||
		formatMessage(optionType ? thinkingTitleByOptionType[optionType] : messages.titleCustom);

	return (
		<Box as="div" xcss={styles.fixedWrapper} testId="remix-thinking-bar">
			<RovoThinkingIndicator title={thinkingTitle} />
			<Inline alignBlock="center" space="space.100">
				<Box as="span" xcss={styles.escBadge}>
					{formatMessage(messages.escHint)}
				</Box>
				<IconButton
					shape="circle"
					onClick={onStop}
					icon={(iconProps) => (
						<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label="" />
					)}
					label={formatMessage(messages.stopLabel)}
				/>
			</Inline>
		</Box>
	);
}
